import { find, VideoConfig } from '@news-mono/web-common'
import {
    ImageV4DTO,
    VideoMetaDTO,
} from '@west-australian-newspapers/publication-types'
import { Logger } from 'typescript-log'
import { sanitiseVideoId } from './sanitise-video-id'

export interface BrightcoveVideoDto extends BrightcovePlayer.Dto {
    account_id: string
}

export function parseSourceFromMetadata(
    source: string | null,
    source_extra: string | null,
) {
    if (!source) {
        return undefined
    }

    const lowerCaseSource = source.toLowerCase()
    switch (lowerCaseSource) {
        case 'other':
        case 'news corp australia':
        case 'radio': {
            return source_extra || source // revert to source if source_extra is empty/undefined
        }
        case 'facebook':
        case 'twitter':
        case 'instagram':
        case 'youtube':
        case 'snapchat':
        case 'linkedin': {
            return source_extra ? `${source} - ${source_extra}` : source // revert to source if source_extra is empty/undefined
        }
        default: {
            return source
        }
    }
}

// As per SWM-4577 - updates the source value based on business logic
export function parseSources(customFields: BrightcovePlayer.DtoCustomFields) {
    if (customFields.ovu_provider_details) {
        // this is done in the aggregator message handler for videos in our database
        return {
            source: customFields.ovu_provider_details.provider_full_name,
        }
    }
    if (customFields.source) {
        return {
            source: customFields.source,
            sourceExtra: customFields.source_extra,
        }
    }

    return null
}

export function parseProducer(dtoProducer?: Stringified<string[]>): string[] {
    if (!dtoProducer) {
        return []
    }
    try {
        const producer = JSON.parse(dtoProducer)
        if (!Array.isArray(producer)) {
            return []
        }
        return producer
    } catch (err) {
        return []
    }
}

function parsePrimaryTopic(meta: BrightcoveVideoDto) {
    if (meta.custom_fields.primary_topic) {
        return meta.custom_fields.primary_topic
    }
    return undefined
}

function parseSecondaryTopics(meta: BrightcoveVideoDto) {
    const topics =
        meta.custom_fields.topic_tags || meta.custom_fields.secondary_topics
    if (topics) {
        return topics.split(',').map((val) => val.trim())
    }
    return []
}

function parsePoster(meta: BrightcoveVideoDto, log: Logger): ImageV4DTO | null {
    const posterIsHTTPS = meta.poster
        ? meta.poster.indexOf('https') === 0
        : false
    if (posterIsHTTPS) {
        return {
            src: meta.poster,
            reference: meta.poster,
            format: '',
            width: 16,
            height: 9,
            dimensions: '16x9',
            ratio: '16:9',
        }
    }

    const firstPosterWithHTTPS = find(
        meta.poster_sources,
        (poster) => poster.src.indexOf('https') === 0,
    )
    if (firstPosterWithHTTPS) {
        return {
            src: firstPosterWithHTTPS.src,
            reference: firstPosterWithHTTPS.src,
            format: '',
            width: 16,
            height: 9,
            dimensions: '16x9',
            ratio: '16:9',
        }
    }

    log.warn(
        {
            id: meta.id,
            account_id: meta.account_id,
            poster_sources: meta.poster_sources,
            poster: meta.poster,
        },
        'No acceptable https poster frame was found for video',
    )

    return null
}

function mapBrightcoveMetadata(
    meta: BrightcoveVideoDto,
    log: Logger,
): VideoMetaDTO {
    const producer = parseProducer(meta.custom_fields.producer)
    const sources = parseSources(meta.custom_fields)
    const posterImage = parsePoster(meta, log)
    const primaryTopic = parsePrimaryTopic(meta)
    const secondaryTopics = parseSecondaryTopics(meta)

    return {
        id: meta.id,
        accountId: meta.account_id,
        source: sources ? sources.source : null,
        sourceExtra:
            sources && sources.sourceExtra ? sources.sourceExtra : null,
        duration: meta.duration,
        name: meta.name,
        longDescription: meta.long_description,
        description: meta.description,
        byline: meta.custom_fields.byline || null,
        producer,
        posterImage,
        primaryTopic: primaryTopic || '',
        secondaryTopics: secondaryTopics,
        videoType: meta.custom_fields.video_type || null,
        videoContentType: meta.custom_fields.video_content_type || null,
        expiryDate: null,
        seriesInfo: meta.series_info,
        publicationDate: meta.created_at,
        canonicalSource: meta.custom_fields.canonical_source || null,
        slug: null,
    }
}

async function getMetadataFromBrightcove(videoId: string, accountId: string) {
    const metadataUrl = `https://edge.api.brightcove.com/playback/v1/accounts/${accountId}/videos/${sanitiseVideoId(
        videoId,
    )}`

    const dto = await fetch(metadataUrl, {
        headers: {
            // See https://support.brightcove.com/overview-policy-api for info about this policy key
            Accept: `application/json;pk=BCpkADawqM0DDw19NyXW8HYv6ywzHzuh20lUOLa8A7-afTvOp0AjT901bCj8pvGQAlbuUPUKrbSRxKJXV7yHBQ2ebGC159vvEAJlBJ4G2VmmEeK-ZuB08VsKKe6ph9narO80eFChm0JXfj1p`,
        },
    }).then<BrightcoveVideoDto>((r) => {
        if (r.status !== 200) {
            throw new Error('Request Failed')
        }
        return r.json()
    })

    return dto
}

async function getMetadataFromContentApi(
    videoId: string,
    config: VideoConfig,
): Promise<VideoMetaDTO> {
    const metadataUrl = `${config.contentApi}/video/meta/${sanitiseVideoId(
        videoId,
    )}`
    const dto = await fetch(metadataUrl).then<VideoMetaDTO>((r) => {
        if (r.status !== 200) {
            throw new Error('Request Failed')
        }
        return r.json()
    })

    return dto
}

export async function getMetadata(
    videoId: string,
    accountId: string,
    config: VideoConfig,
    log: Logger,
) {
    try {
        const videoMetaData = await getMetadataFromContentApi(videoId, config)
        return videoMetaData
    } catch (e) {
        // if the getMetadataFromContentApi call fails, we will try the brightcove endpoint. This
        const meta = await getMetadataFromBrightcove(videoId, accountId)
        return mapBrightcoveMetadata(meta, log)
    }
}
